import { ManageChartTemplateState } from './ManageChartTemplateReducer';
import { RootChartT } from '../CreateOrEditChartTemplate/hooks/getRootChart';

/**
 * Helper to update the context according to the retrieved root chart values.
 * - Reset the authorized softwares
 * - Set the query elements, the viz type, the settings and the authorized functions
 * - Set the title conditionnally (if the action is done from the creation modal)
 */
export const applyRootChartValues = (
    currentChartTemplate: ManageChartTemplateState,
    rootChart: RootChartT,
    rootUrl: string,
    shouldUpdateTitle: boolean,
): ManageChartTemplateState => {
    const { title, metrics, filters, dimensions, settings, vizType } =
        rootChart;
    const newAuthorizedFunctions = Array.from(
        new Set(metrics.map((metric) => metric.functions || []).flat()),
    );

    return {
        ...currentChartTemplate,
        ...(shouldUpdateTitle ? { title: title } : {}),
        metrics,
        filters,
        dimensions,
        rootUrl,
        settings,
        shouldUpdateSourceChartParams: true,
        vizType,
        authorizedSoftwares: [],
        authorizedFunctions: newAuthorizedFunctions,
    };
};

/**
 * Helper to update both authorized softwares and functions when new functions are provided.
 * Indeed, we need to reset authorized softwares when a function is removed from the authorized list.
 */
export const updateAuthorizedFunctions = (
    currentChartTemplate: ManageChartTemplateState,
    newAuthorizedFunctions: string[],
): ManageChartTemplateState => {
    const { authorizedSoftwares, authorizedFunctions } = currentChartTemplate;

    const newAuthorizedSoftwares =
        authorizedFunctions.length > newAuthorizedFunctions.length
            ? []
            : authorizedSoftwares;
    return {
        ...currentChartTemplate,
        authorizedSoftwares: newAuthorizedSoftwares,
        authorizedFunctions: newAuthorizedFunctions,
    };
};

export const SELECT_ALL_OPTION = 'all_selected';

/**
 * Helper to update both authorized softwares.
 * We have to remove all the previously selected softwares in case the select all option gets added.
 * If the user selects specific items whereas it used to be fully selected, we need to remove it.
 */
export const updateAuthorizedSoftwares = (
    currentChartTemplate: ManageChartTemplateState,
    providedAuthorizedSoftwares: string[],
): ManageChartTemplateState => {
    const isSelectAllLatestCheckedOption =
        providedAuthorizedSoftwares[providedAuthorizedSoftwares.length - 1] ===
        SELECT_ALL_OPTION;
    if (isSelectAllLatestCheckedOption)
        return {
            ...currentChartTemplate,
            authorizedSoftwares: [SELECT_ALL_OPTION],
        };
    return {
        ...currentChartTemplate,
        authorizedSoftwares: providedAuthorizedSoftwares.filter(
            (software) => software !== SELECT_ALL_OPTION,
        ),
    };
};
